// #tag: STUDENT VAULT REPOSITORY SCREEN
// #purpose: Secure personal document locker allowing students to upload, organize, scan, preview, reorder, and export certificates, degrees, and identity records.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Trash2, Upload, X } from 'lucide-react';
import { DocumentCard, StatCard } from '../components/common/Cards';
import { Illustration } from '../components/common/Illustration';
import { PageHeaderSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import { VaultPreviewModal } from '../components/vault/VaultPreviewModal';
import { VaultDownloadDialog } from '../components/vault/VaultDownloadDialog';
import { VaultRenameDialog } from '../components/vault/VaultRenameDialog';
import { VaultSequenceDialog } from '../components/vault/VaultSequenceDialog';
import { VaultUploadFlow } from '../components/vault/VaultUploadFlow';
import { resolveIcon } from '../lib/iconMap';
import { vaultDocumentIllustration } from '../lib/vaultIllustration';
import { usePageSections, useVaultCategories, useVaultDocuments } from '../hooks/useExamData';
import { useVaultMutations } from '../hooks/useVault';
import type { VaultDocumentDto } from '../api/cms';
import type { UploadVaultDocumentInput } from '../api/vault';

type UploadMode =
  | { kind: 'create'; categoryId?: string; subcategory?: string }
  | { kind: 'replace'; document: VaultDocumentDto };

const SMART_ATTACH = [
  { label: 'Choose photo', categoryId: 'photos', subcategory: 'Passport Photo' },
  { label: 'Choose signature', categoryId: 'photos', subcategory: 'Signature' },
  { label: 'Choose certificate', categoryId: 'certificates', subcategory: 'OBC' },
] as const;

export function StudentVaultScreen() {
  const gridRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [highlightSubcategory, setHighlightSubcategory] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<UploadMode | null>(null);
  const [previewDoc, setPreviewDoc] = useState<VaultDocumentDto | null>(null);
  const [downloadDoc, setDownloadDoc] = useState<VaultDocumentDto | null>(null);
  const [renameDoc, setRenameDoc] = useState<VaultDocumentDto | null>(null);
  const [sequenceDoc, setSequenceDoc] = useState<VaultDocumentDto | null>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());

  const { data: sectionsData } = usePageSections('vault');
  const headerSection = sectionsData?.data?.[0];
  const { data: categoriesData, isLoading: categoriesLoading } = useVaultCategories();
  const { data: documentsData, isLoading: docsLoading, isError, refetch } = useVaultDocuments({
    q: query || undefined,
    categoryId: activeCategory,
  });
  const { data: allDocumentsData } = useVaultDocuments();
  const { upload, replace, rename, remove, removeMany, reorder, addCategoryItem } = useVaultMutations();

  const categories = categoriesData?.data ?? [];
  const documents = documentsData?.data ?? [];
  const totalVaultDocuments = allDocumentsData?.data?.length ?? documents.length;
  const expiringCount = documents.filter((d) => d.expiryStatus === 'expiring').length;
  const expiredCount = documents.filter((d) => d.expiryStatus === 'expired').length;

  const filteredDocuments = useMemo(() => {
    if (!highlightSubcategory) return documents;
    return documents.filter((d) => d.subcategory === highlightSubcategory);
  }, [documents, highlightSubcategory]);

  useEffect(() => {
    setSelectedIds((current) => {
      const visibleIds = new Set(filteredDocuments.map((doc) => doc.id));
      const next = new Set(Array.from(current).filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [filteredDocuments]);

  const openUpload = (mode: UploadMode) => setUploadMode(mode);

  const handleUploadSubmit = async (input: UploadVaultDocumentInput): Promise<VaultDocumentDto> => {
    if (uploadMode?.kind === 'replace') {
      const updated = await replace.mutateAsync({ id: uploadMode.document.id, input });
      setPreviewDoc(null);
      return updated;
    }
    return upload.mutateAsync(input);
  };

  const handleAddCategoryItem = async (categoryId: string, item: string) => {
    await addCategoryItem.mutateAsync({ categoryId, item });
  };

  const handleDownload = (doc: VaultDocumentDto) => {
    setDownloadDoc(doc);
  };

  const handleRename = async (name: string) => {
    if (!renameDoc) return;
    const updated = await rename.mutateAsync({ id: renameDoc.id, name });
    setPreviewDoc((current) => (current?.id === updated.id ? updated : current));
  };

  const handleSetSequence = async (position: number) => {
    if (!sequenceDoc) return;
    const updated = await reorder.mutateAsync({ id: sequenceDoc.id, position });
    setPreviewDoc((current) => (current?.id === updated.id ? updated : current));
  };

  // #tag: DELETE DOCUMENT CONFIRMATION
  // #purpose: Prevents accidental deletion of a saved document.
  const handleDelete = async (doc: VaultDocumentDto) => {
    if (!window.confirm(`Delete "${doc.name}" from your vault? This cannot be undone.`)) return;
    await remove.mutateAsync(doc.id);
    setPreviewDoc(null);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  const toggleDocumentSelection = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(filteredDocuments.map((doc) => doc.id)));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    const label = ids.length === 1 ? '1 document' : `${ids.length} documents`;
    if (!window.confirm(`Delete ${label} from your vault? This cannot be undone.`)) return;

    await removeMany.mutateAsync(ids);
    setPreviewDoc((current) => (current && selectedIds.has(current.id) ? null : current));
    exitSelectionMode();
  };

  const handleSmartAttach = (categoryId: string, subcategory: string) => {
    setActiveCategory(categoryId);
    setHighlightSubcategory(subcategory);
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if ((categoriesLoading && !categoriesData) || (docsLoading && !documentsData)) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading vault…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load vault." onRetry={() => refetch()} />
      </section>
    );
  }

  const uploadOpen = uploadMode !== null;

  return (
    <section className="adaptive-page wfx-vault-page">
      <div className="wfx-vault-hero">
        {headerSection && (
          <PageHeaderSection section={headerSection} />
        )}

        <div className="wfx-vault-stats wfx-hero-stats">
          <StatCard label="Documents stored" value={documents.length} tone="orange" illustration="vault.stat.stored" />
          <StatCard label="Expiring soon" value={expiringCount} tone="blue" illustration="vault.stat.expiring" />
          <StatCard label="Expired" value={expiredCount} tone="red" illustration="vault.stat.expired" />
        </div>
      </div>

      {/* // #tag: CATEGORY GRID
          // #purpose: Provides compact mobile navigation across all Student Vault document categories. */}
      <div className="wfx-vault-categories">
        {categories.map((cat) => {
          const isActive = activeCategory === cat.id;
          return (
            <button
              key={cat.id}
              type="button"
              className={`wfx-vault-category glass-surface${isActive ? ' is-active' : ''}`}
              onClick={() => {
                setActiveCategory(cat.id);
                setHighlightSubcategory(null);
              }}
            >
              <Illustration
                context={vaultDocumentIllustration(cat.id)}
                size="xs"
                className="wfx-vault-category-illustration"
                loading="eager"
              />
              <strong>{cat.label}</strong>
              <span className="wfx-muted">{cat.items.join(' · ')}</span>
            </button>
          );
        })}
      </div>

      <div className="wfx-search-bar glass-surface">
        <Search size={20} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search documents…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search vault documents"
        />
      </div>

      <div className="wfx-filter-chips">
        <button
          type="button"
          className={`chip-pill ${activeCategory === 'all' ? 'is-active' : ''}`}
          onClick={() => {
            setActiveCategory('all');
            setHighlightSubcategory(null);
          }}
        >
          All documents
        </button>
        {categories.map((cat) => (
          <button
            key={cat.id}
            type="button"
            className={`chip-pill ${activeCategory === cat.id ? 'is-active' : ''}`}
            onClick={() => {
              setActiveCategory(cat.id);
              setHighlightSubcategory(null);
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      <div className="wfx-vault-smart glass-surface">
        <h3>Smart attach</h3>
        <p>When an application form asks for a document, click <strong>Choose from Vault</strong> — done. Documents attach in your saved sequence order.</p>
        <div className="wfx-smart-actions">
          {SMART_ATTACH.map((item) => (
            <button
              key={item.label}
              type="button"
              className={`secondary-action compact${highlightSubcategory === item.subcategory ? ' is-active' : ''}`}
              onClick={() => handleSmartAttach(item.categoryId, item.subcategory)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div ref={gridRef} className="wfx-doc-grid">
        {filteredDocuments.length > 0 && (
          <div className="wfx-vault-bulk-bar glass-surface wfx-doc-grid-full">
            {!selectionMode ? (
              <button
                type="button"
                className="secondary-action compact"
                onClick={() => setSelectionMode(true)}
              >
                Select documents
              </button>
            ) : (
              <>
                <span className="wfx-vault-bulk-count">
                  {selectedIds.size} of {filteredDocuments.length} selected
                </span>
                <div className="wfx-vault-bulk-actions">
                  <button
                    type="button"
                    className="text-action"
                    onClick={selectAllVisible}
                    disabled={selectedIds.size === filteredDocuments.length}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="text-action"
                    onClick={() => setSelectedIds(new Set())}
                    disabled={selectedIds.size === 0}
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    className="secondary-action compact wfx-danger-action"
                    onClick={() => void handleBulkDelete()}
                    disabled={selectedIds.size === 0 || removeMany.isPending}
                  >
                    <Trash2 size={16} aria-hidden="true" />
                    Delete selected
                  </button>
                  <button
                    type="button"
                    className="text-action"
                    onClick={exitSelectionMode}
                    disabled={removeMany.isPending}
                  >
                    <X size={16} aria-hidden="true" /> Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {filteredDocuments.length === 0 ? (
          <EmptyState
            title={documents.length === 0 ? 'No documents yet' : 'No matches in this filter'}
            message={
              documents.length === 0
                ? 'Upload your certificates, photos, and admit cards once — attach them everywhere.'
                : 'Try another category or upload a new document.'
            }
            illustrationContext="empty.vault"
            action={
              <button type="button" className="primary-action compact" onClick={() => openUpload({ kind: 'create' })}>
                <Upload size={16} /> Upload document
              </button>
            }
          />
        ) : (
          filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              highlighted={highlightSubcategory === doc.subcategory}
              selectable={selectionMode}
              selected={selectedIds.has(doc.id)}
              onSelectToggle={() => toggleDocumentSelection(doc.id)}
              onPreview={() => setPreviewDoc(doc)}
              onDownload={() => handleDownload(doc)}
              onRename={() => setRenameDoc(doc)}
              onReplace={() => openUpload({ kind: 'replace', document: doc })}
              onSetSequence={() => setSequenceDoc(doc)}
              onDelete={() => void handleDelete(doc)}
            />
          ))
        )}
      </div>

      <button
        type="button"
        className="wfx-vault-fab"
        aria-label="Quick upload"
        onClick={() => openUpload({ kind: 'create' })}
      >
        <Upload size={24} aria-hidden="true" />
        <span>Quick upload</span>
      </button>

      <VaultUploadFlow
        open={uploadOpen}
        onClose={() => setUploadMode(null)}
        categories={categories}
        existingDocuments={documents}
        mode={uploadMode?.kind === 'replace' ? 'replace' : 'create'}
        replaceDocument={uploadMode?.kind === 'replace' ? uploadMode.document : undefined}
        initialCategoryId={
          uploadMode?.kind === 'replace'
            ? uploadMode.document.categoryId
            : uploadMode?.categoryId ?? 'identity'
        }
        initialSubcategory={
          uploadMode?.kind === 'replace'
            ? uploadMode.document.subcategory
            : uploadMode?.subcategory
        }
        onSubmit={handleUploadSubmit}
        onAddCategoryItem={handleAddCategoryItem}
        onViewDocument={setPreviewDoc}
      />

      <VaultPreviewModal
        document={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={handleDownload}
        onRename={(doc) => setRenameDoc(doc)}
        onReplace={(doc) => {
          setPreviewDoc(null);
          openUpload({ kind: 'replace', document: doc });
        }}
        onSetSequence={(doc) => setSequenceDoc(doc)}
        onDelete={handleDelete}
      />

      <VaultDownloadDialog
        document={downloadDoc}
        onClose={() => setDownloadDoc(null)}
      />

      <VaultRenameDialog
        open={renameDoc !== null}
        initialName={renameDoc?.name ?? ''}
        onClose={() => setRenameDoc(null)}
        onSave={handleRename}
      />

      <VaultSequenceDialog
        open={sequenceDoc !== null}
        documentName={sequenceDoc?.name ?? ''}
        currentPosition={sequenceDoc?.sequenceOrder}
        totalDocuments={totalVaultDocuments}
        onClose={() => setSequenceDoc(null)}
        onSave={handleSetSequence}
      />
    </section>
  );
}
